import { useState } from 'react';
import { Calculator, Heart, DollarSign, Info, ExternalLink, MapPin, ArrowLeft, Coins, TrendingUp, Lock, Wallet } from 'lucide-react';
import { translations } from '../data/translations';
import { useFootprint } from '../hooks/useFootprint';
import { useWeb3 } from '../hooks/useWeb3';

interface ZakatPortalProps {
  onBack: () => void;
  currency: string;
  t: typeof translations['en'];
  isRTL: boolean;
}

export function ZakatPortal({ onBack, currency, t, isRTL }: ZakatPortalProps) {
  const { mintCoin, hablCoins } = useFootprint();
  const { connectWallet, claimTokensOnChain, account, isConnecting, error: web3Error } = useWeb3();
  const [step, setStep] = useState<'intro' | 'calculate' | 'pay' | 'mint' | 'claim'>('intro');
  const [assets, setAssets] = useState({
    cash: 0,
    gold: 0,
    shares: 0,
    debts: 0
  });
  
  // Bitcoin-like Halving Logic Simulation
  const [miningProgress, setMintingProgress] = useState(0);
  const [hablStats] = useState({
    maxSupply: 21000000,
    halvingDate: '2028-04-14',
    difficulty: '14.5 T',
    price: 45000 // Deeds
  });

  const totalAssets = assets.cash + assets.gold + assets.shares;
  const netAssets = Math.max(0, totalAssets - assets.debts);
  const zakatDue = netAssets * 0.025; // 2.5%

  const handleInputChange = (field: keyof typeof assets, value: string) => {
    const num = parseFloat(value) || 0;
    setAssets(prev => ({ ...prev, [field]: num }));
  };

  const startMinting = () => {
    let progress = 0;
    const interval = setInterval(() => {
        progress += 2;
        setMintingProgress(progress);
        if (progress >= 100) {
            clearInterval(interval);
            mintCoin('zakat', 'Zakat Payment Minting', 100);
        }
    }, 50);
  };

  if (step === 'calculate') {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="bg-emerald-900 text-white p-6 pt-safe-top rounded-b-3xl shadow-lg sticky top-0 z-10">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => setStep('intro')} 
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <h2 className="text-xl font-bold">{t.zakatTitle}</h2>
            <div className="w-10"></div>
          </div>
          <div className="text-center pb-4">
            <p className="text-emerald-200 text-sm mb-1">{t.zakatDue}</p>
            <h3 className="text-4xl font-bold">{currency}{zakatDue.toFixed(2)}</h3>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 p-6 space-y-4 overflow-y-auto pb-8 safe-area-bottom">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-stone-100">
            <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{t.cash}</label>
            <div className="flex items-center bg-stone-50 rounded-lg px-4 border border-stone-200 focus-within:border-emerald-500">
              <span className="text-stone-400">{currency}</span>
              <input 
                type="number" 
                className="w-full bg-transparent p-3 outline-none font-semibold text-slate-800"
                value={assets.cash || ''}
                onChange={e => handleInputChange('cash', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-stone-100">
            <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{t.gold}</label>
            <div className="flex items-center bg-stone-50 rounded-lg px-4 border border-stone-200 focus-within:border-emerald-500">
              <span className="text-stone-400">{currency}</span>
              <input 
                type="number" 
                className="w-full bg-transparent p-3 outline-none font-semibold text-slate-800"
                value={assets.gold || ''}
                onChange={e => handleInputChange('gold', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-stone-100">
            <label className="block text-xs font-bold text-stone-500 uppercase mb-2">{t.stocks}</label>
            <div className="flex items-center bg-stone-50 rounded-lg px-4 border border-stone-200 focus-within:border-emerald-500">
              <span className="text-stone-400">{currency}</span>
              <input 
                type="number" 
                className="w-full bg-transparent p-3 outline-none font-semibold text-slate-800"
                value={assets.shares || ''}
                onChange={e => handleInputChange('shares', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-red-50">
            <label className="block text-xs font-bold text-red-400 uppercase mb-2">{t.debts}</label>
            <div className="flex items-center bg-red-50 rounded-lg px-4 border border-red-100 focus-within:border-red-300">
              <span className="text-red-300">{currency}</span>
              <input 
                type="number" 
                className="w-full bg-transparent p-3 outline-none font-semibold text-red-900"
                value={assets.debts || ''}
                onChange={e => handleInputChange('debts', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Action */}
          <button 
            onClick={() => setStep('pay')}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors mt-8 shadow-lg shadow-emerald-200"
          >
            <Heart className="w-5 h-5 fill-emerald-200" />
            {t.pay}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'pay') {
    return (
      <div className="min-h-screen bg-emerald-900 text-white flex flex-col items-center justify-center p-6 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="w-20 h-20 bg-emerald-800 rounded-full flex items-center justify-center mb-6">
          <Heart className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className="text-3xl font-bold mb-2">{t.purify}</h2>
        <p className="text-emerald-200 mb-8 max-w-xs">
          {t.zakatMsg} <strong>{currency}{zakatDue.toFixed(2)}</strong>.
          <br/>{t.choose}
        </p>
        
        <div className="space-y-4 w-full max-w-sm">
          {/* Islamic Relief */}
          <a 
            href="https://www.islamic-relief.org/appeals/zakat/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-white text-emerald-900 font-bold py-4 rounded-xl hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-5 h-5" />
            Islamic Relief (Global)
          </a>

          {/* Local Masjid */}
          <a 
            href="https://www.google.com/maps/search/masjid+near+me" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-emerald-800 border border-emerald-700 text-white font-bold py-4 rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
          >
            <MapPin className="w-5 h-5" />
            Find Local Masjid
          </a>

          <button onClick={() => setStep('calculate')} className="text-emerald-400 text-sm font-semibold mt-4">
            {t.recalculate}
          </button>

          {/* Minting Entry Point */}
          <button 
            onClick={() => {
                setStep('mint');
                startMinting();
            }}
            className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 mt-6 animate-pulse"
          >
            <Coins className="w-5 h-5" />
            Mint Zakat Habl Coin
          </button>
        </div>
      </div>
    );
  }

  if (step === 'mint') {
    return (
        <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 relative overflow-hidden" dir="ltr">
            {/* Background Matrix Rain Effect (Simplified) */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                {Array.from({ length: 20 }).map((_, i) => (
                    <div key={i} className="absolute text-emerald-500 text-[10px] font-mono animate-fall" style={{ left: `${i * 5}%`, top: `-20px`, animationDuration: `${Math.random() * 5 + 2}s` }}>
                        101010101
                    </div>
                ))}
            </div>

            <div className="relative z-10 w-full max-w-sm text-center">
                <div className="mb-8 relative">
                    <div className={`w-32 h-32 mx-auto rounded-full flex items-center justify-center border-4 border-amber-500 bg-amber-500/10 shadow-[0_0_50px_rgba(245,158,11,0.5)] transition-all duration-1000 ${miningProgress < 100 ? 'animate-spin' : ''}`}>
                        <Coins className="w-16 h-16 text-amber-400" />
                    </div>
                    {miningProgress < 100 && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-mono font-bold text-amber-200">
                            {miningProgress}%
                        </div>
                    )}
                </div>

                <h2 className="text-3xl font-bold font-mono mb-2 text-amber-400">
                    {miningProgress < 100 ? 'Minting in Progress...' : 'Habl Coin Minted!'}
                </h2>
                <p className="text-slate-400 text-sm mb-8 font-mono">
                    {miningProgress < 100 ? 'Solving Proof of Goodness...' : 'Block #84923 Verified'}
                </p>

                {/* Bitcoin Logic Stats */}
                <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 space-y-4 mb-8">
                    <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                        <div className="flex items-center gap-2 text-slate-400 text-xs">
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                            <span>Value Growth</span>
                        </div>
                        <span className="font-mono font-bold text-emerald-400">+12.5% / yr</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-left">
                        <div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Max Supply</span>
                            <span className="font-mono font-bold text-white">{hablStats.maxSupply.toLocaleString()}</span>
                        </div>
                        <div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Your Holdings</span>
                            <span className="font-mono font-bold text-white">{hablCoins.toLocaleString()}</span>
                        </div>
                        <div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Halving Date</span>
                            <span className="font-mono font-bold text-amber-400">{hablStats.halvingDate}</span>
                        </div>
                        <div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Difficulty</span>
                            <span className="font-mono font-bold text-purple-400">{hablStats.difficulty}</span>
                        </div>
                    </div>
                </div>

                {miningProgress === 100 && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-emerald-900/30 border border-emerald-500/30 p-4 rounded-xl flex items-center gap-3">
                            <Lock className="w-6 h-6 text-emerald-400" />
                            <div className="text-left">
                                <h4 className="font-bold text-emerald-100 text-sm">Stored in Cold Storage</h4>
                                <p className="text-[10px] text-emerald-300/70">Your Zakat record is immutable.</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setStep('claim')}
                            className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-4 rounded-xl transition-colors mt-2"
                        >
                            Claim on Polygon Chain
                        </button>
                        <button 
                            onClick={onBack}
                            className="w-full bg-transparent hover:bg-slate-800 text-slate-400 font-bold py-4 rounded-xl transition-colors"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
  }

  if (step === 'claim') {
    return (
        <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 text-center">
             <div className="w-24 h-24 bg-purple-900/30 rounded-full flex items-center justify-center mb-6 animate-pulse">
                <Wallet className="w-12 h-12 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Claim HABL on Polygon</h2>
            <p className="text-slate-400 text-sm mb-8 max-w-xs">
                Connect your wallet to mint your {hablCoins} Off-chain HABL coins to the Polygon Mainnet.
            </p>

            {web3Error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl mb-6 text-xs">
                    {web3Error}
                </div>
            )}

            {!account ? (
                <button 
                    onClick={connectWallet}
                    disabled={isConnecting}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-xl transition-all w-full max-w-sm flex items-center justify-center gap-2"
                >
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                </button>
            ) : (
                <div className="space-y-4 w-full max-w-sm">
                    <div className="bg-slate-800 p-4 rounded-xl text-left">
                        <span className="text-xs text-slate-500 block">Connected Wallet</span>
                        <span className="font-mono text-emerald-400 text-sm truncate block">{account}</span>
                    </div>

                    <button 
                        onClick={() => claimTokensOnChain(hablCoins)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-xl transition-all w-full flex items-center justify-center gap-2"
                    >
                        Mint {hablCoins} HABL to Wallet
                    </button>
                </div>
            )}
             
            <button 
                onClick={() => setStep('mint')}
                className="mt-6 text-slate-500 text-sm hover:text-white"
            >
                Back
            </button>
        </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-stone-50 z-50 flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
          <DollarSign className="w-12 h-12 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-3">{t.zakat}</h2>
        <p className="text-stone-500 leading-relaxed max-w-xs mb-8">
          {t.zakatDesc}.
        </p>

        <div className="grid gap-4 w-full max-w-sm">
          <button 
            onClick={() => setStep('calculate')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-colors shadow-lg shadow-emerald-200"
          >
            <Calculator className="w-5 h-5" />
            {t.calculate}
          </button>
          
          <button 
             onClick={onBack}
             className="text-stone-400 font-semibold py-4"
          >
            {t.home}
          </button>
        </div>
      </div>
      
      <div className="p-6 text-center">
        <p className="text-xs text-stone-400 flex items-center justify-center gap-1">
          <Info className="w-3 h-3" /> {t.policy}
        </p>
      </div>
    </div>
  );
}
